import { useAnalytics } from "~/components/Analytics";
import { BuildHTML } from "~/components/BuildHtml/BuildHtml";
import { CTALink } from "~/landing-pages/components/CTA/CTALink/CTALink";
import {
  FeatureHeader,
  FeatureHeaderProps,
} from "~/landing-pages/components/Feature/FeatureHeader/FeatureHeader";
import { LandingPageFeatureWithCTA } from "~/landing-pages/landing-page-types";
import {
  backgroundColorStyle,
  ctaTextColorStyle,
  primaryTextColorStyle,
  secondaryTextColorStyle,
} from "~/landing-pages/shared-landing-page-styles";
import { tagToClassNameMap } from "~/landing-pages/utils/tagToClassNameMap";
import { Body } from "~/ui-library";

export type FeatureWithCTAsProps = FeatureHeaderProps & {
  appearance: "dark" | "light";
  blockIndex: number;
  features: LandingPageFeatureWithCTA[];
};
export const FeatureWithCTAs: React.FC<FeatureWithCTAsProps> = ({
  appearance,
  blockIndex,
  features,
  ...headerProps
}) => {
  const { analyticsEnhancedTrack } = useAnalytics();
  const analyticsVariables = {
    blockIndex,
    appearance,
  };

  const getOnLinkClick =
    ({ ctaLink }: LandingPageFeatureWithCTA) =>
    () => {
      analyticsEnhancedTrack("Clicked Feature Link CTA", {
        ...analyticsVariables,
        linkText: ctaLink.title,
        linkUrl: ctaLink.url,
      });
    };

  return (
    <div
      className={backgroundColorStyle({
        appearance,
        className: "py-12 sm:py-16 lg:py-24",
      })}
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <FeatureHeader appearance={appearance} {...headerProps} />
        <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
          <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-3">
            {features.map((feature) => (
              <div key={feature.name} className="flex flex-col">
                <Body
                  as="dt"
                  bold
                  className={primaryTextColorStyle({ appearance })}
                >
                  {feature.name}
                </Body>
                <Body
                  as="dd"
                  className={secondaryTextColorStyle({
                    appearance,
                    className: "mt-4 flex flex-auto flex-col",
                  })}
                >
                  <div className="flex-auto">
                    <BuildHTML
                      html={feature.description}
                      tagToClassNameMap={tagToClassNameMap}
                    />
                  </div>
                  <div className="mt-6">
                    <CTALink
                      className={ctaTextColorStyle({ appearance })}
                      href={feature.ctaLink.url}
                      onClick={getOnLinkClick(feature)}
                    >
                      {feature.ctaLink.title} <span aria-hidden="true">→</span>
                    </CTALink>
                  </div>
                </Body>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </div>
  );
};
